import { useState, useEffect } from 'react';
import { ArrowLeft, Edit, Trash2, Wrench, Truck as TruckIcon, DollarSign, MapPin, Clock, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { DocumentsTab } from './DocumentsTab';

interface LineItem {
  id: string;
  item_type: 'labor' | 'parts';
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface MaintenanceLog {
  id: string;
  log_number: string;
  maintenance_type: string;
  truck_id: string;
  service_provider: string | null;
  provider_email: string | null;
  provider_phone: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  service_date: string;
  odometer: number | null;
  description: string | null;
  cost: number;
  labor_cost: number | null;
  parts_cost: number | null;
  company_cost_amount: number;
  paid_by?: string;
  driver_payment_amount?: number;
  driver_id?: string;
  gl_account: string;
  created_at: string;
  updated_at: string;
  trucks?: {
    unit_number: string;
    make: string;
    model: string;
    year: number;
    vin: string;
  };
  created_by_profile?: {
    full_name: string;
  };
  drivers?: {
    first_name: string;
    last_name: string;
  };
}

interface MaintenanceLogDetailPageProps {
  logId: string;
  onBack: () => void;
  onEdit: () => void;
}

export function MaintenanceLogDetailPage({ logId, onBack, onEdit }: MaintenanceLogDetailPageProps) {
  const toast = useToast();
  const [log, setLog] = useState<MaintenanceLog | null>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'documents'>('details');

  useEffect(() => {
    loadLog();
  }, [logId]);

  async function loadLog() {
    try {
      const { data, error } = await supabase
        .from('maintenance_logs')
        .select(`
          *,
          trucks(unit_number, make, model, year, vin),
          drivers(first_name, last_name)
        `)
        .eq('id', logId)
        .single();

      if (error) throw error;
      setLog(data);

      // Load line items
      const { data: items, error: itemsError } = await supabase
        .from('maintenance_line_items')
        .select('*')
        .eq('maintenance_log_id', logId)
        .order('item_type', { ascending: false }) // Labor first
        .order('created_at', { ascending: true });

      if (itemsError) {
        console.error('Error loading line items:', itemsError);
      } else {
        setLineItems(items || []);
      }
    } catch (error) {
      console.error('Error loading maintenance log:', error);
      toast.error('Failed to load maintenance log');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('maintenance_logs')
        .delete()
        .eq('id', logId);

      if (error) throw error;

      toast.success('Maintenance log deleted successfully');
      onBack();
    } catch (error) {
      console.error('Error deleting log:', error);
      toast.error('Failed to delete maintenance log');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-slate-600">Maintenance log not found</p>
          <button onClick={onBack} className="mt-4 text-blue-600 hover:underline">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-0 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{log.log_number}</h1>
                <p className="text-sm text-slate-600">{log.maintenance_type}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onEdit}
                className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'documents'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <FileText className="w-4 h-4" />
              Documents
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'details' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
            {/* Log Information */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Wrench className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Service Information</h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Log Number</p>
                  <p className="font-semibold text-slate-900">{log.log_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Maintenance Type</p>
                  <p className="font-semibold text-slate-900">{log.maintenance_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Service Date</p>
                  <p className="font-semibold text-slate-900">
                    {new Date(log.service_date).toLocaleDateString()}
                  </p>
                </div>
                {log.odometer && (
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Odometer</p>
                    <p className="font-semibold text-slate-900">{log.odometer.toLocaleString()} miles</p>
                  </div>
                )}
              </div>
            </div>

            {/* Truck Information */}
            {log.trucks && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <TruckIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">Truck Information</h2>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Unit Number</p>
                    <a
                      href={`/fleet/${log.truck_id}`}
                      className="font-semibold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                    >
                      {log.trucks.unit_number}
                    </a>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Make & Model</p>
                    <p className="font-semibold text-slate-900">
                      {log.trucks.year} {log.trucks.make} {log.trucks.model}
                    </p>
                  </div>
                  {log.trucks.vin && (
                    <div className="col-span-2">
                      <p className="text-sm text-slate-600 mb-1">VIN</p>
                      <p className="font-semibold text-slate-900">{log.trucks.vin}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service Provider */}
            {(log.service_provider || log.provider_email || log.provider_phone) && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Service Provider</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {log.service_provider && (
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Provider Name</p>
                      <p className="font-semibold text-slate-900">{log.service_provider}</p>
                    </div>
                  )}
                  {log.provider_email && (
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Email</p>
                      <p className="font-semibold text-slate-900">{log.provider_email}</p>
                    </div>
                  )}
                  {log.provider_phone && (
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Phone</p>
                      <p className="font-semibold text-slate-900">{log.provider_phone}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service Location */}
            {(log.street || log.city || log.state || log.zip_code) && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">Service Location</h3>
                </div>
                <div className="space-y-2">
                  {log.street && <p className="text-slate-900">{log.street}</p>}
                  <p className="text-slate-900">
                    {[log.city, log.state, log.zip_code].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}

            {/* Line Items Table */}
            {lineItems.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Service Details</h3>
                
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b-2 border-slate-300">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Type</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Description</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Qty</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Rate</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.filter(i => i.item_type === 'labor').map((item) => (
                      <tr key={item.id} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm font-medium text-blue-600">Labor</td>
                        <td className="py-3 px-4 text-sm text-slate-900">{item.description}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">{item.quantity.toFixed(2)}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">${item.rate.toFixed(2)}</td>
                        <td className="py-3 px-4 text-sm text-right font-medium text-slate-900">${item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                    {lineItems.filter(i => i.item_type === 'labor').length > 0 && (
                      <tr className="bg-slate-50">
                        <td colSpan={4} className="py-2 px-4 text-sm font-semibold text-right text-slate-700">
                          Labor Subtotal
                        </td>
                        <td className="py-2 px-4 text-sm text-right font-semibold text-slate-900">
                          ${log.labor_cost?.toFixed(2) || '0.00'}
                        </td>
                      </tr>
                    )}
                    
                    {lineItems.filter(i => i.item_type === 'parts').map((item) => (
                      <tr key={item.id} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm font-medium text-green-600">Parts</td>
                        <td className="py-3 px-4 text-sm text-slate-900">{item.description}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">{item.quantity.toFixed(2)}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">
                          {item.rate > 0 ? `$${item.rate.toFixed(2)}` : 'Included'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium text-slate-900">
                          {item.amount > 0 ? `$${item.amount.toFixed(2)}` : 'Included'}
                        </td>
                      </tr>
                    ))}
                    {lineItems.filter(i => i.item_type === 'parts').length > 0 && (
                      <tr className="bg-slate-50">
                        <td colSpan={4} className="py-2 px-4 text-sm font-semibold text-right text-slate-700">
                          Parts Subtotal
                        </td>
                        <td className="py-2 px-4 text-sm text-right font-semibold text-slate-900">
                          ${log.parts_cost?.toFixed(2) || '0.00'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Financial Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Financial</h3>
              </div>

              <div className="space-y-4">
                {log.labor_cost !== null && log.labor_cost > 0 && (
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Labor</p>
                    <p className="text-lg font-semibold text-slate-900">
                      ${log.labor_cost.toFixed(2)}
                    </p>
                  </div>
                )}
                {log.parts_cost !== null && log.parts_cost > 0 && (
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Parts</p>
                    <p className="text-lg font-semibold text-slate-900">
                      ${log.parts_cost.toFixed(2)}
                    </p>
                  </div>
                )}
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-1">Total Cost</p>
                  <p className="text-xl font-semibold text-slate-900">
                    ${log.cost.toFixed(2)}
                  </p>
                </div>

                {/* Payment Breakdown - Show when driver is paying */}
                {log.paid_by === 'driver' && log.driver_payment_amount && log.driver_payment_amount > 0 && (
                  <>
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">Driver Payment</p>
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-semibold text-orange-600">
                          ${log.driver_payment_amount.toFixed(2)}
                        </p>
                        {log.drivers && (
                          <p className="text-xs text-slate-500">
                            {log.drivers.first_name} {log.drivers.last_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">Company Payment</p>
                      <p className="text-lg font-semibold text-green-600">
                        ${log.company_cost_amount.toFixed(2)}
                      </p>
                    </div>
                  </>
                )}

                <div className="pt-4 border-t-2 border-slate-900">
                  <p className="text-sm text-slate-600 mb-1">
                    {log.paid_by === 'driver' ? 'Company Balance Due' : 'Balance Due'}
                  </p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${log.company_cost_amount.toFixed(2)}
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-1">GL Account</p>
                  <p className="font-semibold text-slate-900">{log.gl_account}</p>
                </div>
              </div>
            </div>

            {/* Activity */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-slate-100 p-3 rounded-lg">
                  <Clock className="w-6 h-6 text-slate-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Activity</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Created By</p>
                  <p className="font-semibold text-slate-900">
                    {log.created_by_profile?.full_name || 'System'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {new Date(log.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-1">Last Updated</p>
                  <p className="text-xs text-slate-500">
                    {new Date(log.updated_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        ) : (
          <DocumentsTab maintenanceLogId={logId} />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Delete Maintenance Log?</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete log <strong>{log.log_number}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
